/**
 * Frontend RBAC helper — mirrors backend `core/permissions.py`.
 *
 * Permissions on a user document are a list of strings:
 *   • "content"           — legacy group-wide grant (all actions for that group).
 *   • "content:create"    — granular (group:action).
 *
 * Super-admin (role==='admin') passes every check.
 */

const ALL_ACTION_KEYS = new Set(['view', 'create', 'edit', 'delete', 'publish']);
// Member accounts ('interpreter') never have granular admin permissions; every
// other non-admin role (council, asistente, custom roles) does.
const NON_GRANULAR_ROLES = new Set(['admin', 'interpreter']);
const isGranularRole = (role) => !!role && !NON_GRANULAR_ROLES.has(role);

export function canAction(user, group, action) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (!isGranularRole(user.role)) return false;
  if (!ALL_ACTION_KEYS.has(action)) return false;
  const perms = user.permissions || [];
  for (const raw of perms) {
    if (typeof raw !== 'string' || !raw) continue;
    if (raw.includes(':')) {
      const [g, a] = raw.split(':', 2);
      if (g.trim() === group && a.trim() === action) return true;
    } else if (raw.trim() === group) {
      // Legacy: grant every action defined for this group.
      return true;
    }
  }
  return false;
}

export function canGroup(user, group) {
  // Returns true if the user has ANY action for the group.
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (!isGranularRole(user.role)) return false;
  const perms = user.permissions || [];
  for (const raw of perms) {
    if (typeof raw !== 'string' || !raw) continue;
    if (raw.includes(':')) {
      const [g] = raw.split(':', 2);
      if (g.trim() === group) return true;
    } else if (raw.trim() === group) {
      return true;
    }
  }
  return false;
}
